/**
 * 2026-10-06 · the ten-tab editor's "Add Ingredients" dialog, on a material step: what kind of
 * material, how its quantity scales, the allowed deviation, critical RM, where it comes from and
 * goes to, how it is executed, and the verification rules beside them. Saved on the step
 * (`ingredient`, services/recipe/ingredient_settings.py). The runner judges the allowed deviation
 * (the weighing tolerance) and a single-lot strategy; the rest are recorded with the step.
 */
import React from "react";
import { Flex, Input, InputNumber, Segmented, Select, Switch } from "antd";
import { Field } from "./Field";
import { MachineSidePicker } from "./machines";
import { useSamplingLists } from "./lists";
import { MON_DEVIATION } from "./MonitoringForm";

export const INGREDIENT_STEP_TYPES = ["DISPENSING", "MATERIAL_CHARGE", "CONTROLLED_ADDITION", "TRANSFER"];
const ING_RULES = [
  { title: "QR verification", items: [["verify_material_code", "Material code"], ["verify_quantity", "Quantity"]] },
  { title: "Verification methods", items: [["method_qr", "QR scan"], ["method_barcode", "Barcode"], ["method_scale", "Scale / weight"]] },
  { title: "Verification policy", items: [["policy_identity", "Material identity"], ["policy_quantity_tolerance", "Quantity tolerance"],
    ["policy_photo", "Photo capture"], ["policy_kitting", "Kitting verified"], ["policy_double_verification", "Double verification"],
    ["policy_reconciliation", "Material reconciliation"]] },
];

export const ING_MOVEMENT = { DISPENSING: "Dispensing", MATERIAL_CHARGE: "Charging", CONTROLLED_ADDITION: "Charging", TRANSFER: "Transfer" };

export function IngredientSettings({ step, bomLine, readOnly, onChange, part = "settings", materialSlot = null, showErrors = false }) {
  const ing = step.ingredient || {};
  const put = (patch) => onChange({ ingredient: { ...ing, ...patch } });
  const params = useSamplingLists().parameters;
  const lineTol = bomLine?.tolerance ? String(bomLine.tolerance).replace(/[±%\s]/g, "") : null;
  const sw = (key, label) => (
    <label key={key} className="u0-ingsw">
      <span>{label}</span>
      <Switch size="small" disabled={readOnly} checked={!!ing[key]} checkedChildren="Yes" unCheckedChildren="No"
              onChange={(v) => put({ [key]: v })} />
    </label>
  );
  // The verification switches are rules of the step, so they sit under "Rules bound here".
  if (part === "rules") {
    return (
      <Flex vertical gap={8} style={{ marginBottom: 10 }}>
        {ING_RULES.map((g) => (
          <div key={g.title} className="u0-ingbox">
            <span className="u0-eyebrow">{g.title}</span>
            <div className="u0-ingsws">{g.items.map(([k, l]) => sw(k, l))}</div>
          </div>
        ))}
        {/* The dialog's Charging Strategy panel: QR / scale charging with their method, the lot
            strategy, and how a multi-lot charge is split. */}
        <div className="u0-ingbox">
          <span className="u0-eyebrow">Charging strategy</span>
          <Flex vertical gap={8}>
            {ing.method_qr ? (
              <Segmented size="small" disabled={readOnly} value={ing.charging_mode_qr || "Single QR"}
                         options={["Single QR", "Multi QR"]} onChange={(v) => put({ charging_mode_qr: v })} />
            ) : null}
            {ing.method_scale ? (
              <Segmented size="small" disabled={readOnly} value={ing.charging_mode_scale || "Single Stretch"}
                         options={["Single Stretch", "Multi Stretch"]} onChange={(v) => put({ charging_mode_scale: v })} />
            ) : null}
            <Segmented size="small" disabled={readOnly} value={ing.lot_strategy || "MULTI"}
                       onChange={(v) => put({ lot_strategy: v })}
                       options={[{ value: "SINGLE", label: "Single lot" }, { value: "MULTI", label: "Multi lot" }]} />
            {(ing.lot_strategy || "MULTI") === "MULTI" ? (
              <div className="u0-grid2">
                <Field label="Number of lots">
                  <InputNumber size="small" min={1} disabled={readOnly} value={ing.number_of_lots ?? null} style={{ width: "100%" }}
                               onChange={(v) => put({ number_of_lots: v })} />
                </Field>
                <Field label="Lot distribution">
                  <Select size="small" disabled={readOnly} value={ing.fill_strategy || "max_fill"}
                          options={[{ value: "max_fill", label: "Max fill" }, { value: "uniform", label: "Uniform" }]}
                          onChange={(v) => put({ fill_strategy: v })} />
                </Field>
              </div>
            ) : null}
          </Flex>
        </div>
      </Flex>
    );
  }
  // 2026-10-12 · the activity: Dispensing, Charging or Transfer. Stated on the step when the
  // author chose one; otherwise read off the step type as before (a charge is Charging).
  // 2026-10-29 (owner) · a TRANSFER step is a transfer: Movement type is Transfer, fixed (not chosen).
  const fixedMovement = step.step_type === "TRANSFER";
  const movement = fixedMovement ? "Transfer" : (ing.movement_type || ING_MOVEMENT[step.step_type] || "Charging");
  // 2026-10-29 (owner) · a TRANSFER step's form follows /process-config's Add Ingredients (Ingredients.js):
  // Material type required (RM until changed), RM picked from the bill, a WIPM / Product named ("RM Name"),
  // execution mode required. Material charge and controlled addition are unchanged.
  const transfer = step.step_type === "TRANSFER";
  const materialType = transfer ? (ing.material_type || "RM") : ing.material_type;
  const namedMaterial = transfer && (materialType === "WIPM" || materialType === "Product");
  return (
    <Flex vertical gap={12}>
      <div className="u0-grid2">
        <Field label="Movement type" required={transfer && !readOnly} hint="What the step does with the material: Dispensing (weighed out in the booth), Charging (added to the vessel) or Transfer (moved from one vessel to another). Empty: read off the step type.">
          <Select size="small" disabled={readOnly || fixedMovement} value={movement}
                  onChange={(v) => put({ movement_type: v === (ING_MOVEMENT[step.step_type] || "Charging") && !ing.movement_type ? null : v })}
                  options={[{ value: "Dispensing", label: "Dispensing" }, { value: "Charging", label: "Charging" }, { value: "Transfer", label: "Transfer" }]} />
        </Field>
        {/* 2026-10-29 (owner) · as /process-config's Add Ingredients: Material type is required (RM until changed);
            RM is picked from the bill of materials, a WIPM / Product is named ("RM Name"). */}
        <Field label="Material type" required={transfer && !readOnly}>
          <Select size="small" allowClear={!transfer} disabled={readOnly} value={materialType} placeholder="— choose —"
                  onChange={(v) => put({ material_type: v ?? null, ...(transfer && v === "RM" ? { wip_material_name: null } : {}) })}
                  options={[{ value: "RM", label: "RM — raw material" }, { value: "WIPM", label: "WIPM — work in progress" }, { value: "Product", label: "Product" }]} />
        </Field>
      </div>
      {namedMaterial ? (
        <Field label="RM Name" required={!readOnly}>
          <Input size="small" disabled={readOnly} value={ing.wip_material_name || ""} maxLength={200} placeholder="Enter rm name"
                 status={showErrors && !readOnly && !String(ing.wip_material_name || "").trim() ? "error" : undefined}
                 onChange={(e) => put({ wip_material_name: e.target.value })} />
          {showErrors && !readOnly && !String(ing.wip_material_name || "").trim() ? (
            <span className="u0-fielderr" role="alert">Name the material — the step cannot be saved without it.</span>
          ) : null}
        </Field>
      ) : materialSlot}
      <div className="u0-grid2">
        <Field label="Allowed deviation" hint="The weighing tolerance for this step. Empty = the bill-of-materials line's tolerance.">
          <InputNumber size="small" min={0} max={100} step={0.1} disabled={readOnly} style={{ width: "100%" }} addonAfter="%"
                       value={ing.deviation_percent == null ? null : Number(ing.deviation_percent)}
                       placeholder={lineTol ? `bill: ± ${lineTol}` : "bill's tolerance"}
                       onChange={(v) => put({ deviation_percent: v ?? null })} />
        </Field>
        <Field label="Critical RM (CMA)">
          <div>
            <Switch size="small" disabled={readOnly} checked={!!ing.critical_rm} checkedChildren="Yes" unCheckedChildren="No"
                    onChange={(v) => put({ critical_rm: v })} />
          </div>
        </Field>
        <Field label="Dispense type (execution mode)" required={transfer && !readOnly}
               hint="Manual — the operator types the tare and gross weight. Automatic — only “Start weighing”: the weight is read from the balance, nothing can be typed. Semi-automatic — read from the balance, typed (with a witness) only if it cannot be read.">
          <Select size="small" allowClear disabled={readOnly} value={ing.execution_mode} placeholder="— choose —"
                  status={transfer && showErrors && !readOnly && !ing.execution_mode ? "error" : undefined}
                  onChange={(v) => put({ execution_mode: v ?? null })}
                  options={[{ value: "Manual", label: "Manual" }, { value: "Semi-Automatic", label: "Semi-automatic" }, { value: "Automatic", label: "Automatic" }]} />
        </Field>
        <Field label="Source equipment" hint="Where the material comes from — machine category → group → machines.">
          <MachineSidePicker value={ing.from_machine} readOnly={readOnly} onChange={(v) => put({ from_machine: v })} />
        </Field>
        <Field label="Destination equipment"
               hint={movement === "Charging" ? "Where it is charged. Empty: the equipment chosen for the step under \"Where it perform\"." : "Where it is moved to."}>
          <MachineSidePicker value={ing.to_machine} readOnly={readOnly} onChange={(v) => put({ to_machine: v })}
                             placeholder={movement === "Charging" && (step.booths || []).length ? `the step's equipment (${step.booths.length})` : undefined} />
        </Field>
      </div>
      <div className="u0-ingbox">
        <span className="u0-eyebrow">Conditional parameter</span>
        <div className="u0-grid2">
          <Field label="Parameter">
            <Select size="small" allowClear showSearch optionFilterProp="label" disabled={readOnly} placeholder="— none —"
                    value={ing.cond_parameter_id ?? undefined} options={params}
                    onChange={(v) => put({ cond_parameter_id: v ?? null, ...(v ? {} : { cond_min: null, cond_max: null, cond_deviation_rule: null }) })} />
          </Field>
          {ing.cond_parameter_id ? (
            <>
              <Field label={<>Range<span className="u0-req"> *</span></>}>
                <div className="u0-mon-pair">
                  <InputNumber size="small" disabled={readOnly} placeholder="min" value={ing.cond_min == null ? null : Number(ing.cond_min)}
                               onChange={(v) => put({ cond_min: v })} />
                  <span className="u0-mon-dash">–</span>
                  <InputNumber size="small" disabled={readOnly} placeholder="max" value={ing.cond_max == null ? null : Number(ing.cond_max)}
                               onChange={(v) => put({ cond_max: v })} />
                </div>
              </Field>
              <Field label="Deviation rule">
                <Select size="small" disabled={readOnly} value={ing.cond_deviation_rule || "none"} options={MON_DEVIATION}
                        onChange={(v) => put({ cond_deviation_rule: v })} />
              </Field>
            </>
          ) : null}
        </div>
      </div>
    </Flex>
  );
}
